import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Download,
  CheckCircle2,
  FileText,
  Loader2,
  Star,
  Sparkles,
  Shield,
} from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/mice-case-studies-2025";
const TITLE = "5 MICE Case Studies from 2025 — Free PDF | Trawelldmc";
const DESCRIPTION =
  "Free PDF — 5 detailed MICE case studies from 2025: Dubai 1,800-pax conference, Singapore 60-day F1 incentive, Bali 350-pax channel launch + Asia 2026 trends + how to write a winning MICE RFP.";

const HIGHLIGHTS = [
  {
    icon: FileText,
    title: "3 operating case studies",
    note: "Dubai 1,800-pax conference · Singapore 360-pax F1-weekend incentive · Bali 350-pax channel launch — each with full KPI strips.",
  },
  {
    icon: Sparkles,
    title: "2 industry briefings",
    note: "MICE Asia 2026 trends report + a practical guide to writing a MICE RFP that gets you the best Asia proposals.",
  },
  {
    icon: Shield,
    title: "Procurement-grade evidence",
    note: "Real NPS, retention, sell-in lift and rebook numbers. Use as proof points when you brief shortlists internally.",
  },
];

const COVERS = [
  { kpi: "96/100", label: "Delegate NPS", caption: "Dubai · 1,800-pax 5-day conference" },
  { kpi: "60 days", label: "Lead Time", caption: "Singapore · F1-weekend 360-pax incentive" },
  { kpi: "4×", label: "Engagement Uplift", caption: "Bali · 350-pax channel partner launch" },
  { kpi: "11", label: "Asian Markets", caption: "Trawelldmc operating footprint 2026" },
];

const FAQS = [
  {
    q: "What's actually in the PDF?",
    a: "A 10-page A4 booklet: cover, table of contents, three full case studies with brief / solution / outcomes / takeaway, plus a 7-trend Asia MICE 2026 briefing and a 7-section RFP guide. Real KPIs, real hotel names, no marketing fluff.",
  },
  {
    q: "Is this gated?",
    a: "We ask for your name and work email so we can send relevant case studies to you in the future. We don't share your details and you can unsubscribe in one click. The download is free.",
  },
  {
    q: "Can I share this with my procurement team?",
    a: "Yes — please do. The PDF is licensed for internal redistribution within your organisation. If your team has 5+ people we'd love to brief them on a video call too.",
  },
];

export default function CaseStudiesLeadMagnet() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    consent: true,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState("");
  const [download, setDownload] = useState(null);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/case-studies`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setDownload(data);
      setStatus("done");
      // Auto-trigger the download.
      const a = document.createElement("a");
      a.href = data.download_url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setErrorMsg(err.message || "Something went wrong.");
      setStatus("error");
    }
  };

  return (
    <div data-testid="case-studies-lead-magnet">
      <Seo
        path={PATH}
        title={TITLE}
        description={DESCRIPTION}
        breadcrumb={[
          { name: "Home", path: "/" },
          { name: "Free MICE Case Studies", path: PATH },
        ]}
      />

      {/* HERO + FORM */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="overline !text-[#FF5238] mb-5">Free download · 10-page PDF</div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.04]">
              5 MICE case studies <br />
              from 2025.
              <br />
              <span className="text-[#FF5238]">The receipts.</span>
            </h1>
            <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
              Operating numbers from real 2025 MICE programmes — Dubai,
              Singapore, Bali. Plus a 2026 Asia MICE trends briefing and a
              practical RFP guide. Built for procurement teams and corporate
              event leads who care about evidence.
            </p>

            {/* Mini KPI strip */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl">
              {COVERS.map((c) => (
                <div key={c.caption} className="border-l-2 border-[#FF5238] pl-3">
                  <div className="font-heading text-2xl font-light">{c.kpi}</div>
                  <div className="text-xs text-white/55 uppercase tracking-wider mt-1">
                    {c.label}
                  </div>
                  <div className="text-xs text-white/40 mt-1.5 leading-snug">
                    {c.caption}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex items-center gap-3 text-sm text-white/55">
              <div className="flex">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-[#FF5238] text-[#FF5238]" />
                ))}
              </div>
              <span>140,000+ pax operated · 11 Asian markets · 20+ years</span>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="lg:col-span-5">
            <div className="bg-white text-[#0B192C] rounded-md shadow-2xl p-7 md:p-9" data-testid="lead-magnet-form-card">
              {status !== "done" ? (
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <div className="overline !text-[#FF5238] mb-1">Send me the PDF</div>
                    <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight">
                      Tell us who&apos;s reading.
                    </h2>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                      Full name
                    </label>
                    <input
                      type="text"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Riya Sharma"
                      className="w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#FF5238]"
                      data-testid="lm-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                      Work email
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="riya@yourcompany.com"
                      className="w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#FF5238]"
                      data-testid="lm-email"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                        Company
                      </label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Your company"
                        className="w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#FF5238]"
                        data-testid="lm-company"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                        Role
                      </label>
                      <input
                        type="text"
                        value={form.role}
                        onChange={update("role")}
                        placeholder="Head of Events"
                        className="w-full border border-slate-300 px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#FF5238]"
                        data-testid="lm-role"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="lm-submit"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Preparing your PDF…
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Download the PDF
                      </>
                    )}
                  </button>

                  {status === "error" && (
                    <p className="text-sm text-red-600" data-testid="lm-error">
                      {errorMsg || "Something went wrong. Please try again."}
                    </p>
                  )}

                  <p className="text-xs text-slate-500 leading-relaxed">
                    We&apos;ll use your email to send relevant case studies. No
                    spam. Unsubscribe any time. By submitting you accept our
                    privacy practices.
                  </p>
                </form>
              ) : (
                <div data-testid="lm-success" className="text-center py-6">
                  <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#FF5238]/10 text-[#FF5238]">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-light tracking-tight mt-5">
                    Your PDF is on the way.
                  </h2>
                  <p className="text-slate-600 mt-3 leading-relaxed text-sm">
                    The download should have started automatically. If it
                    didn&apos;t, use the button below.
                  </p>
                  <a
                    href={download?.download_url || "/trawelldmc-mice-case-studies-2025.pdf"}
                    download={download?.filename || "trawelldmc-mice-case-studies-2025.pdf"}
                    className="btn-primary w-full justify-center mt-6"
                    data-testid="lm-manual-download"
                  >
                    <Download className="h-4 w-4" />
                    Download the PDF
                  </a>
                  <div className="mt-6 pt-6 border-t border-slate-200 text-left">
                    <div className="overline !text-[#FF5238] mb-2 text-xs">Ready to brief us?</div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      Skip the back-and-forth. Submit a brief and our senior
                      team will respond within 48 hours.
                    </p>
                    <Link to="/inquiry?type=MICE" className="btn-secondary w-full justify-center mt-4" data-testid="lm-post-cta">
                      Submit a brief <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">Inside the PDF</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Three case studies. <br />Two briefings. <br />
            <span className="text-[#FF5238]">Zero fluff.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="card-flat p-8">
              <h.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {h.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{h.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">FAQ</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Quick answers.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
            {FAQS.map((f) => (
              <div key={f.q}>
                <h3 className="font-heading text-lg font-medium text-[#0B192C] tracking-tight">
                  {f.q}
                </h3>
                <p className="text-slate-600 mt-3 leading-relaxed text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
